import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FileUpload from "@/components/FileUpload";
import DoctorCalendar from "@/components/DoctorCalendar";
import DoctorIntegrations from "@/components/DoctorIntegrations";
import DoctorReferralTab from "@/components/DoctorReferralTab";
import { toast } from "sonner";
import { CreditCard, CalendarDays, Sparkles, X, Save, FileText, Plus, Trash2 } from "lucide-react";
import { formatDateShort, formatDateTimeParts, statusLabel } from "@/lib/utils";

const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = (i % 2) * 30;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

// Iter14 P5.3 (2026-02-27): HolidaysDoctorSection removed per user request.
// Automatic holiday blocking has also been disabled on the backend
// (_is_holiday_closed → always False). Doctors control agenda via schedule
// + días libres (ausencias) only.

/**
 * "Días feriados" — per-doctor holiday configuration.
 * Default off = closed; toggle "Trabajo los feriados por defecto" flips it globally.
 * Each upcoming applicable holiday (national or matching the doctor's city) can
 * override the default and optionally set a special start/end time.
 */
// Iter14 P5.3 (2026-02-27): HolidaysDoctorSection removed entirely per user
// request. Auto holiday blocking has been disabled on the backend as well
// (_is_holiday_closed returns False). See CHANGELOG.

export default function DoctorDashboard() {
  const { t, lang, user, setUser } = useApp();
  // Tab is URL-driven (`/doctor/panel/:tab`) so back button + refresh land on
  // the same tab and never bounce the doctor outside the panel.
  const { tab: tabParam } = useParams();
  const nav = useNavigate();
  const VALID_TABS = ["appointments", "availability", "invoices", "referral", "integrations", "profile"];
  const tab = VALID_TABS.includes(tabParam) ? tabParam : "appointments";
  const setTab = (v) => nav(`/doctor/panel/${v}`);
  const [me, setMe] = useState(null);
  const [apts, setApts] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const [invoicesList, setInvoicesList] = useState([]);
  const [newDayOff, setNewDayOff] = useState({ start: "", end: "", reason: "" });
  const [schedule, setSchedule] = useState(() =>
    Array.from({ length: 7 }, (_, d) => ({
      day: d, enabled: d < 5, start: "09:00", end: "17:00", duration_min: 30, breaks: [],
    })));

  const load = () => {
    api.get("/doctor/me").then(r => setMe(r.data));
    api.get("/doctor/appointments").then(r => setApts(r.data));
    api.get("/doctor/days-off").then(r => setDaysOff(r.data.days_off || [])).catch(() => {});
    api.get("/doctor/invoices").then(r => setInvoicesList(r.data || [])).catch(() => {});
    api.get("/doctor/schedule").then(r => {
      const cfg = r.data.schedule_config || [];
      if (cfg.length) {
        setSchedule((prev) => prev.map((d) => cfg.find(c => c.day === d.day) || d));
      }
    });
  };
  useEffect(() => { load(); }, []);

  const updateDay = (di, patch) => setSchedule((s) => s.map((d, i) => i === di ? {...d, ...patch} : d));
  const addBreak = (di) => updateDay(di, { breaks: [...schedule[di].breaks, { start: "12:00", end: "14:00" }] });
  const removeBreak = (di, bi) => updateDay(di, { breaks: schedule[di].breaks.filter((_, i) => i !== bi) });
  const updateBreak = (di, bi, patch) => updateDay(di, {
    breaks: schedule[di].breaks.map((b, i) => i === bi ? {...b, ...patch} : b)
  });

  const saveSchedule = async () => {
    await api.put("/doctor/schedule", { days: schedule });
    toast.success("Horario guardado");
    load();
  };

  const cancelApt = async (apt_id) => {
    await api.post(`/doctor/appointments/${apt_id}/cancel`);
    toast.success("Cita cancelada");
    load();
  };

  const [cgvChecked, setCgvChecked] = useState(false);
  const [cgvBusy, setCgvBusy] = useState(false);

  // CGV: only require acceptance when the doctor hasn't accepted the CURRENT
  // published version. Bumping AGENDIK_CGV_VERSION on the backend makes the
  // checkbox reappear automatically without any frontend deploy.
  const cgvCurrent = me?.cgv_current_version;
  const cgvAlreadyAccepted = !!(me?.cgv_accepted_at && me?.cgv_accepted_version && me?.cgv_accepted_version === cgvCurrent);

  const startCheckout = async () => {
    if (!cgvAlreadyAccepted && !cgvChecked) return toast.error("Debe aceptar las Condiciones Generales de Venta.");
    setCgvBusy(true);
    try {
      if (!cgvAlreadyAccepted) {
        await api.post("/doctor/accept-cgv", { version: cgvCurrent });
      }
      const r = await api.post("/payments/checkout", { lookup_key: "agendik_monthly", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setCgvBusy(false); }
  };

  // No-card trial (Feb 2026): "Agregar método de pago" opens a Stripe Checkout
  // session in setup mode with the merchant's PaymentMethodConfiguration (so
  // card, Apple Pay, Google Pay, Amazon Pay, Link all appear). Visible during
  // the trial and when the subscription is PAUSED by Stripe at trial end.
  const openPaymentSetup = async () => {
    try {
      const r = await api.post("/doctor/payment-setup", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      // The backend returns the exact Stripe error (incl. invalid PMC ID) — never hide it.
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // "Gestionar mi suscripción" opens the Stripe-hosted Customer Portal.
  const openBillingPortal = async () => {
    try {
      const r = await api.post("/doctor/billing-portal", { origin_url: window.location.origin });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (!me) return <div className="max-w-6xl mx-auto p-8 text-slate-500">{t.common.loading}</div>;

  const isTrial = me.subscription_status === "trial" || me.subscription_status === "trialing";
  const isActive = me.subscription_status === "active";
  const isPaused = me.subscription_status === "paused";
  const isPending = me.status === "pending";
  const isRejected = me.status === "rejected";
  const trialEnds = me.trial_ends_at ? formatDateShort(me.trial_ends_at) : "";
  const trialDaysLeft = me.trial_ends_at ? Math.max(0, Math.ceil((new Date(me.trial_ends_at) - new Date()) / 86400000)) : 0;

  const upcoming = apts.filter(a => a.status !== "cancelled" && new Date(a.date + "T" + a.time) >= new Date()).sort((a,b) => (a.date+a.time).localeCompare(b.date+b.time));
  const past = apts.filter(a => a.status === "cancelled" || new Date(a.date + "T" + a.time) < new Date());

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      {isPending && (
        <div data-testid="pending-banner" className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          <strong>Cuenta pendiente de verificación.</strong> Nuestro equipo está revisando tu SENESCYT o diploma. Recibirás un email al aprobarse.
        </div>
      )}
      {isRejected && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm">
          <strong>Cuenta rechazada.</strong> Contacta a soporte para más detalles.
        </div>
      )}
      {!me.id_number && (
        <div data-testid="missing-id-banner" className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          <strong>Completa tu identificación.</strong> Añade tu cédula (10 dígitos) o pasaporte desde
          la pestaña <em>{t.doctorDash.profile}</em> · sección <em>Datos de facturación</em>. Es necesario para emitir tus facturas.
        </div>
      )}
      {me.referral_code && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs">
          🎁 Registrado con código <strong>{me.referral_code}</strong>
          {me.referral_affiliate_name && <> · Tu contacto: <strong>{me.referral_affiliate_name}</strong></>}
          {" · "}{trialDaysLeft} días de prueba restantes · primer cobro el {formatDateShort(me.trial_ends_at)}
        </div>
      )}
      {me?.needs_card_relink && (
        <div data-testid="card-relink-banner"
             className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <CreditCard size={22} className="text-amber-600 mt-0.5 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900">Confirma tu tarjeta bancaria</div>
            <p className="text-sm text-amber-800 mt-1">
              Tu suscripción se creó antes de la activación de los pagos en producción. Vuelve a ingresar tu tarjeta para no interrumpir el servicio al final del período de prueba. Tu fecha de fin de prueba <strong>no cambia</strong>.
            </p>
          </div>
          <Button data-testid="card-relink-btn" onClick={startCheckout}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0">
            <CreditCard size={14} className="mr-1"/> Ingresar mi tarjeta
          </Button>
        </div>
      )}
      {me?.past_due_since && (
        <div data-testid="past-due-banner"
             className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
          <CreditCard size={22} className="text-red-600 mt-0.5 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-red-900">Tu pago ha fallado, actualiza tu tarjeta</div>
            <p className="text-sm text-red-800 mt-1">
              No pudimos cobrar tu suscripción. Actualiza tu tarjeta bancaria ahora — tu perfil se ocultará de la búsqueda pública si no regularizas el pago en 14 días.
            </p>
          </div>
          <Button data-testid="past-due-btn" onClick={startCheckout}
                  className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0">
            <CreditCard size={14} className="mr-1"/> Actualizar mi tarjeta
          </Button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t.doctorDash.welcome} {me.full_name}</h1>
          <p className="text-slate-500 text-sm mt-1">{me.specialty} · {me.city}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isTrial && (
            <Badge data-testid="trial-badge" className="bg-amber-100 text-amber-800 border-amber-200">
              <Sparkles size={12} className="mr-1"/> {t.doctorDash.trial} · {t.doctorDash.trialEnds} {trialEnds}
            </Badge>
          )}
          {isActive && <Badge data-testid="active-badge" className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ {t.doctorDash.active}</Badge>}
          {!isActive && isPending && (
            <Button data-testid="subscribe-pending-btn" disabled
                    className="bg-slate-300 text-slate-500 cursor-not-allowed hover:bg-slate-300">
              <CreditCard size={16} className="mr-2"/> Suscripción disponible tras la verificación
            </Button>
          )}
          {isPaused && (
            <Badge data-testid="paused-badge" className="bg-slate-200 text-slate-700 border-slate-300">
              En pausa — agregue un método de pago
            </Badge>
          )}
          {/* No-card trial (Feb 2026): "Agregar método de pago" visible during trial AND when paused. */}
          {(isTrial || isPaused) && !isPending && !isRejected && (
            <Button data-testid="add-payment-method-btn" onClick={openPaymentSetup}
                    className="bg-blue-600 hover:bg-blue-700">
              <CreditCard size={16} className="mr-2"/> Agregar método de pago
            </Button>
          )}
          {/* "Gestionar mi suscripción" → Stripe Customer Portal. Shown whenever the
              doctor has an active Stripe subscription (active / trial / paused / past_due). */}
          {!isPending && !isRejected && me.stripe_subscription_id && (
            <button data-testid="billing-portal-btn" onClick={openBillingPortal}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
              Gestionar mi suscripción
            </button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="dashboard-tabs" className="flex w-full overflow-x-auto no-scrollbar gap-1 bg-slate-100 p-1 h-auto justify-start rounded-md">
          <TabsTrigger data-testid="tab-appointments" value="appointments" className="whitespace-nowrap flex-shrink-0">{t.doctorDash.upcoming}</TabsTrigger>
          <TabsTrigger data-testid="tab-availability" value="availability" className="whitespace-nowrap flex-shrink-0">{t.doctorDash.availability}</TabsTrigger>
          <TabsTrigger data-testid="tab-invoices" value="invoices" className="whitespace-nowrap flex-shrink-0">Mis facturas</TabsTrigger>
          <TabsTrigger data-testid="tab-referral" value="referral" className="whitespace-nowrap flex-shrink-0">Programa de referidos</TabsTrigger>
          <TabsTrigger data-testid="tab-integrations" value="integrations" className="whitespace-nowrap flex-shrink-0">Integraciones</TabsTrigger>
          <TabsTrigger data-testid="tab-profile" value="profile" className="whitespace-nowrap flex-shrink-0">{t.doctorDash.profile}</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="mt-5 space-y-6">
          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarDays size={18}/> {t.doctorDash.upcoming}</h2>
            {upcoming.length === 0 ? <p className="text-slate-500 text-sm">{t.doctorDash.noAppointments}</p> : (
              <div className="space-y-2">
                {upcoming.map(a => (
                  <div key={a.id} data-testid={`apt-row-${a.id}`}
                       className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50">
                    <div>
                      <div className="font-medium text-slate-900">{a.patient_name}</div>
                      <div className="text-xs text-slate-500">{a.patient_email} · {a.patient_phone}</div>
                    </div>
                    <div className="text-sm text-slate-700 font-medium">{formatDateTimeParts(a.date, a.time)}</div>
                    <Button data-testid={`apt-cancel-${a.id}`} size="sm" variant="outline"
                            onClick={() => cancelApt(a.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50">
                      <X size={14} className="mr-1"/> {t.doctorDash.cancel}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarDays size={18}/> {t.doctorDash.calendarTab}</h2>
            <DoctorCalendar apts={apts} schedule={schedule} onRefresh={load} doctorId={me.id}/>
          </CardContent></Card>

          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3">{t.doctorDash.past}</h2>
            {past.length === 0 ? <p className="text-slate-500 text-sm">–</p> : (
              <div className="space-y-2">
                {past.slice(0, 20).map(a => (
                  <div key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100">
                    <div className="text-sm text-slate-700">{a.patient_name} <span className="text-slate-400">· {formatDateTimeParts(a.date, a.time)}</span></div>
                    <Badge variant="outline" className="text-xs">{statusLabel(a.status, lang)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-5">
          <DoctorIntegrations doctorId={me.id} doctor={me} onRefresh={load}/>
        </TabsContent>

        <TabsContent value="availability" className="mt-5 space-y-6">
          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3">Días libres (ausencias)</h2>
            <p className="text-xs text-slate-500 mb-3">Selecciona el día de inicio y el día de fin. Durante una ausencia, ningún paciente puede reservar.</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Fecha de inicio</label>
                <Input type="date" data-testid="dayoff-start"
                       value={newDayOff.start} onChange={e => setNewDayOff({...newDayOff, start: e.target.value})}/>
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Fecha de fin</label>
                <Input type="date" data-testid="dayoff-end"
                       value={newDayOff.end} onChange={e => setNewDayOff({...newDayOff, end: e.target.value})}/>
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Motivo (opcional)</label>
                <Input placeholder="Motivo (opcional)" data-testid="dayoff-reason"
                       value={newDayOff.reason} onChange={e => setNewDayOff({...newDayOff, reason: e.target.value})}/>
              </div>
              <div className="flex items-end">
                <Button data-testid="dayoff-add" onClick={async () => {
                  if (!newDayOff.start || !newDayOff.end) return toast.error("Fechas requeridas");
                  // Day-level absences: start at 00:00 of start date, end at 00:00 of day AFTER end date
                  // so that the whole end date is covered.
                  const startISO = `${newDayOff.start}T00:00:00`;
                  const endDateObj = new Date(newDayOff.end + "T00:00:00");
                  endDateObj.setDate(endDateObj.getDate() + 1);
                  const endISO = endDateObj.toISOString().slice(0,10) + "T00:00:00";
                  try {
                    const r = await api.post("/doctor/days-off", { start: startISO, end: endISO, reason: newDayOff.reason });
                    if ((r.data.conflicts || []).length) {
                      toast.warning(`${r.data.conflicts.length} cita(s) en conflicto — revísalas manualmente.`);
                    } else toast.success("Ausencia agregada");
                    setNewDayOff({ start: "", end: "", reason: "" });
                    const l = await api.get("/doctor/days-off"); setDaysOff(l.data.days_off || []);
                  } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
                }} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"><Plus size={14} className="mr-1"/>Agregar</Button>
              </div>
            </div>
            <div className="space-y-2">
              {daysOff.length === 0 && <p className="text-sm text-slate-500">Sin ausencias.</p>}
              {daysOff.map(d => (
                <div key={d.id} data-testid={`dayoff-${d.id}`}
                     className="flex items-center justify-between p-2 rounded border border-slate-200">
                  <div className="text-sm">
                    <div className="font-medium text-slate-900">{d.start.slice(0,10)} → {d.end.slice(0,10)}</div>
                    {d.reason && <div className="text-xs text-slate-500">{d.reason}</div>}
                  </div>
                  <Button data-testid={`dayoff-del-${d.id}`} size="sm" variant="ghost"
                          onClick={async () => {
                            await api.delete(`/doctor/days-off/${d.id}`);
                            setDaysOff(daysOff.filter(x => x.id !== d.id));
                          }} className="text-red-600 hover:bg-red-50">
                    <Trash2 size={14}/>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-5 space-y-6">
          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText size={18}/> Mis facturas
            </h2>
            {invoicesList.length === 0 ? <p className="text-sm text-slate-500">Aún sin facturas.</p> : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500 uppercase">
                  <tr><th className="py-2">N°</th><th>Fecha</th><th>Período</th><th>Monto</th><th></th></tr>
                </thead>
                <tbody>
                  {invoicesList.map(inv => (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.number}`} className="border-t border-slate-100">
                      <td className="py-2 font-mono text-xs">{inv.number}</td>
                      <td>{formatDateShort(inv.paid_at)}</td>
                      <td className="text-xs text-slate-500">
                        {inv.period_start ? new Date(inv.period_start*1000).toLocaleDateString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric"}) : "-"}
                        {" → "}
                        {inv.period_end ? new Date(inv.period_end*1000).toLocaleDateString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric"}) : "-"}
                      </td>
                      <td className="font-semibold">${inv.amount_usd.toFixed(2)}</td>
                      <td className="text-right">
                        {inv.invoice_pdf && <a data-testid={`invoice-pdf-${inv.number}`} href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline text-xs">PDF Stripe</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-[11px] text-slate-500 mt-3">
              Emisor : A GOOD JOB, SASU · SIREN 888632510 · Agendik · TVA non applicable – article 259-1 du CGI.
            </p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="referral" className="mt-5">
          <DoctorReferralTab/>
        </TabsContent>

        <TabsContent value="availability" className="mt-5">
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-slate-900">{t.doctorDash.availability}</h2>
                <p className="text-xs text-slate-500">Hora local Ecuador (UTC-5) · sin cambio de horario de verano</p>
              </div>
              <Button data-testid="save-schedule-btn" onClick={saveSchedule} className="bg-blue-600 hover:bg-blue-700">
                <Save size={14} className="mr-1"/> Guardar horario
              </Button>
            </div>
            <div className="space-y-3">
              {schedule.map((d, di) => (
                <div key={di} data-testid={`day-block-${di}`}
                     className={`rounded-xl border p-3 ${d.enabled ? "border-blue-100 bg-blue-50/40" : "border-slate-200 bg-slate-50"}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" data-testid={`day-toggle-${di}`}
                             checked={d.enabled}
                             onChange={e => updateDay(di, { enabled: e.target.checked })}
                             className="w-4 h-4 accent-blue-600"/>
                      <span className="font-semibold text-slate-800 text-sm w-14">{t.days[di]}</span>
                    </div>
                    {d.enabled && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <select data-testid={`start-${di}`} value={d.start}
                                onChange={e => updateDay(di, { start: e.target.value })}
                                className="h-8 rounded-md border border-slate-200 px-2 text-xs">
                          {HALF_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <span className="text-slate-400 text-xs">→</span>
                        <select data-testid={`end-${di}`} value={d.end}
                                onChange={e => updateDay(di, { end: e.target.value })}
                                className="h-8 rounded-md border border-slate-200 px-2 text-xs">
                          {HALF_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <select data-testid={`duration-${di}`} value={d.duration_min}
                                onChange={e => updateDay(di, { duration_min: Number(e.target.value) })}
                                className="h-8 rounded-md border border-slate-200 px-2 text-xs">
                          {[5,10,15,20,30,45,60].map(m => <option key={m} value={m}>{m} min</option>)}
                        </select>
                        <Button size="sm" variant="outline" onClick={() => addBreak(di)}
                                data-testid={`add-break-${di}`}
                                className="h-8 text-xs">+ Pausa</Button>
                      </div>
                    )}
                  </div>
                  {d.enabled && d.breaks.length > 0 && (
                    <div className="pl-6 mt-2 space-y-1">
                      {d.breaks.map((b, bi) => (
                        <div key={bi} className="flex items-center gap-2">
                          <span className="text-xs text-amber-700">Pausa:</span>
                          <select value={b.start} onChange={e => updateBreak(di, bi, { start: e.target.value })}
                                  className="h-7 rounded-md border border-slate-200 px-2 text-xs">
                            {HALF_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                          <span className="text-slate-400 text-xs">→</span>
                          <select value={b.end} onChange={e => updateBreak(di, bi, { end: e.target.value })}
                                  className="h-7 rounded-md border border-slate-200 px-2 text-xs">
                            {HALF_HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                          <button data-testid={`rm-break-${di}-${bi}`}
                                  onClick={() => removeBreak(di, bi)}
                                  className="text-red-500 hover:text-red-700"><X size={14}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">La agenda del paciente se ajusta automáticamente a esta configuración.</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="profile" className="mt-5">
          <Card><CardContent className="p-5 space-y-4">
            <div><Label>Nombre</Label><Input value={me.full_name} onChange={e => setMe({...me, full_name: e.target.value})}/></div>
            <div><Label>Clínica</Label><Input value={me.clinic_name} onChange={e => setMe({...me, clinic_name: e.target.value})}/></div>
            <div><Label>Dirección</Label><Input value={me.address} onChange={e => setMe({...me, address: e.target.value})}/></div>
            <div><Label>Teléfono</Label><Input value={me.phone} onChange={e => setMe({...me, phone: e.target.value})}/></div>
            <div><Label>Foto de perfil</Label>
              <FileUpload testid="profile-photo" label="Subir foto de perfil"
                          accept="image/jpeg,image/png,image/webp" maxMB={5}
                          preview={me.photo_url}
                          onData={async (d) => {
                            await api.post("/doctor/upload", { field: "photo_url", data_url: d });
                            setMe({...me, photo_url: d}); toast.success("Foto actualizada");
                          }}/>
            </div>

            {/* Presentation */}
            <div className="pt-3 border-t border-slate-100">
              <Label>{t.docFields.presentationLabel}</Label>
              <textarea data-testid="profile-presentation" rows={3} maxLength={300}
                        value={me.presentation || ""}
                        onChange={e => setMe({...me, presentation: e.target.value})}
                        className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none resize-none"/>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1">
                <span>{t.docFields.presentationHelp}</span>
                <span>{t.docFields.presentationCounter.replace("{n}", String((me.presentation||"").length))}</span>
              </div>
            </div>

            {/* Payment methods */}
            <div className="pt-3 border-t border-slate-100">
              <Label>{t.docFields.paymentsTitle}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                {[
                  {id:"card_credit", label:t.docFields.pmCredit},
                  {id:"card_debit",  label:t.docFields.pmDebit},
                  {id:"cash",        label:t.docFields.pmCash},
                  {id:"transfer",    label:t.docFields.pmTransfer},
                  {id:"deuna",       label:"DeUna"},
                  {id:"other",       label:t.common.other || "Otro"},
                ].map(pm => {
                  const on = (me.payment_methods||[]).includes(pm.id);
                  return (
                    <label key={pm.id} data-testid={`pm-${pm.id}`}
                           className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${on?"border-blue-400 bg-blue-50":"border-slate-200 bg-white"}`}>
                      <input type="checkbox" checked={on}
                             onChange={e => {
                               const list = new Set(me.payment_methods||[]);
                               if (e.target.checked) list.add(pm.id); else list.delete(pm.id);
                               setMe({...me, payment_methods: [...list]});
                             }}
                             className="accent-blue-600"/>
                      <span>{pm.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Price */}
            <div>
              <Label>{t.docFields.priceLabel}</Label>
              <Input data-testid="profile-price" type="number" min="0" step="1" placeholder={t.docFields.pricePlaceholder}
                     value={me.consultation_price_usd ?? ""}
                     onChange={e => setMe({...me, consultation_price_usd: e.target.value === "" ? null : Number(e.target.value)})}/>
            </div>

            {/* Insurances */}
            <div>
              <Label>{t.docFields.insurancesTitle}</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="profile-insurance-input" list="agendik-insurances-list" placeholder={t.docFields.insurancesAdd}
                       value={me._newInsurance || ""}
                       onChange={e => setMe({...me, _newInsurance: e.target.value})}
                       onKeyDown={e => {
                         if (e.key === "Enter") {
                           e.preventDefault();
                           const v = (me._newInsurance||"").trim();
                           if (!v) return;
                           setMe({...me, insurances: [...(me.insurances||[]), v].slice(0,20), _newInsurance:""});
                         }
                       }}/>
                <datalist id="agendik-insurances-list">
                  {["IESS","Salud S.A.","Humana","BMI","Confiamed","Ecuasanitas","Bupa","Best Doctors","Pan-American Life","Mediken","Otro","Ninguno (solo particular)"].map(s => <option key={s} value={s}/>)}
                </datalist>
                <Button type="button" size="sm" variant="outline" data-testid="profile-insurance-add"
                        onClick={() => {
                          const v = (me._newInsurance||"").trim();
                          if (!v) return;
                          setMe({...me, insurances: [...(me.insurances||[]), v].slice(0,20), _newInsurance:""});
                        }}>{t.docFields.add}</Button>
              </div>
              {(me.insurances||[]).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(me.insurances||[]).map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-3 py-1 text-xs">
                      {s}
                      <button type="button" onClick={() => setMe({...me, insurances: me.insurances.filter((_,j) => j !== i)})}
                              className="text-slate-400 hover:text-red-600" aria-label="remove">×</button>
                    </span>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 mt-3 text-sm">
                <input type="checkbox" data-testid="profile-iess" checked={!!me.accepts_iess}
                       onChange={e => setMe({...me, accepts_iess: e.target.checked})}
                       className="accent-blue-600"/>
                <span>{t.docFields.iessLabel}</span>
              </label>
            </div>

            {/* Datos de facturación (iter14) — identity + invoicing block. Kept
                distinct from the public profile because RUC / billing address
                only surface on Stripe invoices, not on the doctor's page. */}
            <div className="pt-4 mt-4 border-t border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Datos de facturación</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Título</Label>
                  <Select value={me.title || "Dr."} onValueChange={v => setMe({...me, title: v})}>
                    <SelectTrigger data-testid="profile-title-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dr.">Dr.</SelectItem>
                      <SelectItem value="Dra.">Dra.</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>Tipo de identificación</Label>
                  <Select value={me.id_type || "cedula"} onValueChange={v => setMe({...me, id_type: v})}>
                    <SelectTrigger data-testid="profile-id-type-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cedula">Cédula</SelectItem>
                      <SelectItem value="pasaporte">Pasaporte</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>N° {me.id_type === "pasaporte" ? "pasaporte" : "cédula"} *</Label>
                  <Input data-testid="profile-id-number" value={me.id_number || ""}
                         onChange={e => setMe({...me, id_number: (me.id_type === "cedula"
                           ? e.target.value.replace(/\D/g,"").slice(0,10)
                           : e.target.value.toUpperCase().slice(0,20))})}/></div>
                <div><Label>RUC</Label>
                  <Input data-testid="profile-ruc" value={me.ruc || ""} maxLength={13}
                         placeholder="1234567890001"
                         onChange={e => setMe({...me, ruc: e.target.value.replace(/\D/g,"").slice(0,13)})}/></div>
                <div className="sm:col-span-2"><Label>Nombre o razón social (facturación)</Label>
                  <Input data-testid="profile-billing-name" value={me.billing_name || ""}
                         placeholder={me.full_name || "Nombre para la factura"}
                         onChange={e => setMe({...me, billing_name: e.target.value})}/></div>
                <div><Label>RUC de facturación</Label>
                  <Input data-testid="profile-billing-ruc" value={me.billing_ruc || ""} maxLength={13}
                         onChange={e => setMe({...me, billing_ruc: e.target.value.replace(/\D/g,"").slice(0,13)})}/></div>
                <div><Label>Email de facturación</Label>
                  <Input data-testid="profile-billing-email" type="email" value={me.billing_email || ""}
                         onChange={e => setMe({...me, billing_email: e.target.value})}/></div>
                <div><Label>Dirección de facturación</Label>
                  <Input data-testid="profile-billing-address" value={me.billing_address || ""}
                         onChange={e => setMe({...me, billing_address: e.target.value})}/></div>
                <div><Label>Teléfono de facturación</Label>
                  <Input data-testid="profile-billing-phone" value={me.billing_phone || ""}
                         onChange={e => setMe({...me, billing_phone: e.target.value})}/></div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Estos datos se usan para tus facturas Stripe · A GOOD JOB, SASU (Agendik).</p>
            </div>

            <Button data-testid="profile-save-btn" onClick={async () => {
              try {
                const r = await api.put("/doctor/me", {
                  title: me.title, full_name: me.full_name, clinic_name: me.clinic_name, address: me.address,
                  phone: me.phone, photo_url: me.photo_url,
                  presentation: me.presentation || "",
                  payment_methods: me.payment_methods || [],
                  consultation_price_usd: me.consultation_price_usd ?? null,
                  insurances: me.insurances || [],
                  accepts_iess: !!me.accepts_iess,
                  id_type: me.id_type || "cedula", id_number: me.id_number || "",
                  ruc: me.ruc || "",
                  billing_name: me.billing_name || "",
                  billing_ruc: me.billing_ruc || "",
                  billing_address: me.billing_address || "",
                  billing_phone: me.billing_phone || "",
                  billing_email: me.billing_email || "",
                });
                setMe({...r.data, _newInsurance: ""}); toast.success("Perfil guardado");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Error");
              }
            }} className="bg-blue-600 hover:bg-blue-700">{t.doctorDash.save}</Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
